import Backbone from 'backbone'
import _ from 'lodash'
import highlight from 'scoreboard/components/highlight'
import config from 'scoreboard/components/config'
import globals from './globals'

var i18n = {}

Backbone.on('load', function(data) {
	if (data.i18n) {
		i18n.data = data.i18n
	}
})

_.extend(i18n, {

	/**
	 * Translates a message resource key and performs substitutions. The key is
	 * specified as the first argument, and any {0}, {1}, etc placeholders that
	 * need to be substituted in can be supplied as additional arguments.
	 * Returns the given string if no matching resource key is found.
	 *
	 * @param {String| Object} key
	 *            the resource bundle key to be translated, or an object structured
	 *            like the java object MessageSourceResolvable
	 * @param arguments
	 *            additional arguments may be specified to supply substitutions
	 * @return {String} the translation
	 */
	get: function(/** String */ key) {
		// Handle passing in a serialized form of MessageSourceResolvable
		if (_.isObject(key)) {
			let substituteArguments =
				arguments[1]?.substituteArguments ?? true
			// Resolve the args as well since they could be codes themselves
			// #62981 but not when substituteArguments == false
			let codeArgs = _.map(key.arguments || [], arg => {
				return substituteArguments ? this.get(arg) : arg
			})
			return this.get.call(this, key.codes[0], ...codeArgs)
		}

		var translated = _.map(arguments, function(arg) {
			return this.data[arg] || arg
		}.bind(this))

		// Perform replacements before substitutions to ensure that user input
		// is unaffected by the configuration options
		var result = this.replaceConfigurableTerminology(translated[0], key)
		return this.substitute.apply(this, [result, ...translated.slice(1)])
	},


	/**
	 * Translates a message resource key and performs substitutions. The key is
	 * specified as the first argument, and any {0}, {1}, etc placeholders that
	 * need to be substituted in can be supplied as additional arguments.
	 * Returns the given string if no matching resource key is found.
	 * Does NOT apply configurable terminology changes
	 *
	 * @param {String| Object} key
	 *            the resource bundle key to be translated, or an object structured
	 *            like the java object MessageSourceResolvable
	 * @param arguments
	 *            additional arguments may be specified to supply substitutions
	 * @return {String} the translation
	 */
	getWithoutTerminologyChanges: function(/** String */ key) {
		// Handle passing in a serialized form of MessageSourceResolvable
		if (_.isObject(key) && arguments.length == 1) {
			// Resolve the args as well since they could be codes themselves
			let codeArgs = _.map(key.arguments || [], this.getWithoutTerminologyChanges.bind(this))
			return this.getWithoutTerminologyChanges.call(this, key.codes[0], ...codeArgs)
		}

		var translated = _.map(arguments, function(arg) {
			return this.data[arg] || arg
		}.bind(this))

		// Apply substitutions directly without configurable terminology replacements
		return this.substitute.apply(this, translated)
	},


	/**
	 * Performs substitutions in a pre-translated message. The key is
	 * specified as the first argument, and any {0}, {1}, etc placeholders that
	 * need to be substituted in can be supplied as additional arguments.
	 *
	 * @param str
	 *            the pre-translated text
	 * @param arguments
	 *            additional arguments may be specified to supply substitutions
	 * @return the string with substitutions applied
	 */
	substitute: function(/** String */ str) {
		// If the passed-in term is null or undefined, just return it unchanged
		if (!str) {
			return str
		}
		str = '' + str
		for (var i=1; i<arguments.length; i++) {
			var substitution = _.isUndefined(arguments[i]) ? '' : arguments[i]
			str = str.split('\{' + (i-1) + '\}').join(substitution)
		}
		return str
	},

	/**
	 * Returns a message like "0 metrics". In MessageResources.properties define
	 * the keys like this
	 *
	 * scorecards.node.metric.number.singular=1 Metric
	 * scorecards.node.metric.number.plural={0} Metrics
	 *
	 * Then call this method like this (note .singular and .plural are stripped
	 * from the key):
	 *
	 * i18n.countMessage('scorecards.node.metric.number', numMetrics)
	 *
	 * @param keyPrefix
	 * @param count
	 * @param additionalArgs if additional args are required pass them here.
	 * This must include the count in the correct position
	 */
	countMessage: function(keyPrefix, count, ...additionalArgs) {
		const key = keyPrefix + '.' + (count === 1 ? 'singular' : 'plural')
		const args = additionalArgs.length > 0 ? additionalArgs : [count]
		return this.get(key, ...args)
	},

	/**
	 * Returns whether a key exists
	 */
	hasKey: function(key) {
		return !!i18n.data[key]
	},

	/**
	 * Takes a message code and optional args and resolves them applying
	 * highlighting to each arg.
	 *
	 * @param {Object|String} code A message code resolvable object or message
	 * code
	 * @param [Array|...*] args Optional array of args, or varags of arguments for
	 * the message code
	 * @return String the resolved message with arguments highlighted
	 */
	highlight: function(message) {
		if (_.isNil(message)) { return null }
		if (_.isString(message)) {
			let messageArgs = [...arguments].slice(1)
			if (arguments.length == 2 && _.isArray(arguments[1])) {
				messageArgs = arguments[1]
			}
			return this.highlight({ codes: [message], arguments: messageArgs })
		}

		let code = _.get(message, 'codes[0]', null)
		if (_.isNil(code)) { return null }
		message.arguments = _.map(message.arguments, arg => {
			// if the arg is an empty string, we don't want to wrap it in a
			// span, which will create extra space
			return _.isEmpty(arg) ? arg : highlight(arg)
		})
		return this.get(message, {
			// #62981 don't do substitutions or terminology changes on arguments,
			// since they may have user-entered text
			substituteArguments: false
		})
	},

	/**
	 * Replaces specific terms in a string with the corresponding configuration values.
	 * This method uses a placeholder-based approach to handle cases where one
	 * term might be a substring of another (see GitHub issue #62709).
	 *
	 * @param {String} str - The string to perform replacements on
	 * @param {String} key - The key associated with the replacement (used for filtering)
	 * @return {String} The string with replacements made
	 */
	replaceConfigurableTerminology: function(str, key) {
		// Don't replace things which begin with admin.appConfiguration to
		// prevent loops and ensure the settings page remains unchanged
		if (!str || typeof str != 'string' ||
			(typeof key === 'string' && key.startsWith('admin.appConfiguration.methodology'))) {
			return str
		}

		const referTo = globals.referToReplacements

		// Quick exit if no replacement configuration is available
		if (!referTo) {
			return str
		}

		// Get the configuration for all replacements
		const replacements = this._getReplacementConfig(referTo)

		// Prepare the replacement data with translations
		const processedReplacements = []

		for (const replacement of replacements) {
			const translatedKey = this.get(replacement.key)

			// Skip replacements that are empty or unchanged
			if (!replacement.value || replacement.value === translatedKey) {
				continue
			}

			processedReplacements.push({
				original: translatedKey,
				replacement: replacement.value,
				length: translatedKey.length  // For sorting by length
			})
		}

		// Sort by length in descending order to handle substring issues
		// (e.g., longer terms are replaced before shorter ones that might be substrings)
		processedReplacements.sort((a, b) => b.length - a.length)

		// Apply the placeholder approach to handle all replacements
		return this._applyPlaceholderReplacements(str, processedReplacements)
	},

	/**
	 * Returns the configuration for all possible terminology replacements.
	 * This defines the mapping between i18n keys and their replacement values.
	 *
	 * @param {Object} referTo - The referTo configuration object containing replacement values
	 * @return {Array} Array of replacement configuration objects
	 * @private
	 */
	_getReplacementConfig: function(referTo) {
		return [
			{ key: 'admin.appConfiguration.methodology.objective', value: referTo.referToObjectiveAs },
			{ key: 'admin.appConfiguration.methodology.objectives', value: referTo.referToObjectivesAs },
			{ key: 'admin.appConfiguration.methodology.initiative', value: referTo.referToInitiativeAs },
			{ key: 'admin.appConfiguration.methodology.initiatives', value: referTo.referToInitiativesAs },
			{ key: 'admin.appConfiguration.methodology.task', value: referTo.referToTaskAs },
			{ key: 'admin.appConfiguration.methodology.tasks', value: referTo.referToTasksAs },
			{ key: 'admin.appConfiguration.methodology.milestone', value: referTo.referToMilestoneAs },
			{ key: 'admin.appConfiguration.methodology.milestones', value: referTo.referToMilestonesAs },
			{ key: 'admin.appConfiguration.methodology.KPI', value: referTo.referToKpiAs },
			{ key: 'admin.appConfiguration.methodology.KPIs', value: referTo.referToKpisAs },
			{ key: 'admin.appConfiguration.methodology.keyPerformanceArea', value: referTo.referToPerspectiveAs },
			{ key: 'admin.appConfiguration.methodology.keyPerformanceAreas', value: referTo.referToPerspectivesAs },
			{ key: 'admin.appConfiguration.methodology.genericItem', value: referTo.referToGenericItemAs },
			{ key: 'admin.appConfiguration.methodology.genericItems', value: referTo.referToGenericItemsAs },
			{ key: 'admin.appConfiguration.methodology.measure', value: referTo.referToKpiAs },
			{ key: 'admin.appConfiguration.methodology.measures', value: referTo.referToKpisAs },
			{ key: 'admin.appConfiguration.methodology.metric', value: referTo.referToKpiAs },
			{ key: 'admin.appConfiguration.methodology.metrics', value: referTo.referToKpisAs },
			{ key: 'admin.appConfiguration.methodology.perspective', value: referTo.referToPerspectiveAs },
			{ key: 'admin.appConfiguration.methodology.perspectives', value: referTo.referToPerspectivesAs },
			{ key: 'admin.appConfiguration.methodology.theme', value: referTo.referToGenericItemAs },
			{ key: 'admin.appConfiguration.methodology.themes', value: referTo.referToGenericItemsAs }
		]
	},

	/**
	 * Applies replacements using a placeholder approach to handle substring issues.
	 * This is a helper method for replaceConfigurableTerminology.
	 *
	 * @param {String} str - The string to perform replacements on
	 * @param {Array} replacements - Array of replacement objects
	 * @return {String} The string with replacements made
	 * @private
	 */
	_applyPlaceholderReplacements: function(str, replacements) {
		if (!str || !replacements.length) {
			return str
		}

		// First pass: Replace terms with placeholders
		let result = str
		const placeholderMap = {}

		for (let i = 0; i < replacements.length; i++) {
			const item = replacements[i]
			const placeholder = `[[TERM_${i}]]`

			// Store the placeholder to value mapping
			placeholderMap[placeholder] = this.matchWordCasing(item.original, item.replacement)
			placeholderMap[placeholder.toLowerCase()] = item.replacement

			// Replace whole words only, preserving case
			result = this.replaceAllOccurrencesPreservingCase(result, item.original, placeholder)
		}

		// Second pass: Replace placeholders with their final values
		Object.keys(placeholderMap).forEach(placeholder => {
			result = result.split(placeholder).join(placeholderMap[placeholder])
		})

		return result
	},

	/**
	 * Replaces all occurrences of a target string with a replacement string,
	 * preserving the original case. This method ensures only whole words are replaced
	 * and handles case preservation (see GitHub issue #62709).
	 *
	 * @param {String} str - The original string
	 * @param {String} search - The target string to replace
	 * @param {String} replace - The replacement string
	 * @return {String} The modified string with replacements
	 */
	replaceAllOccurrencesPreservingCase: function(str, search, replace) {
		if (!str || !search || !replace) {
			return str
		}

		let result = ''
		let startIndex = 0
		const lowerStr = str.toLowerCase()
		const lowerSearch = search.toLowerCase()

		while (true) {
			// Find the next occurrence of the search term
			const searchIndex = lowerStr.indexOf(lowerSearch, startIndex)
			if (searchIndex === -1) {
				// No more occurrences, add the rest of the string
				result += str.slice(startIndex)
				break
			}

			const endIndex = searchIndex + search.length

			// Check if this is a whole word and not in a substitution placeholder
			if (this._isWholeWordMatch(str, searchIndex, endIndex) &&
				!this._isPartOfSubstitutionPlaceholder(str, searchIndex, endIndex)) {

				// Add the text before the match
				result += str.slice(startIndex, searchIndex)

				// Get the original case of the term
				const originalTerm = str.slice(searchIndex, endIndex)

				// Match the case of the replacement to the original term
				result += this.matchWordCasing(originalTerm, replace)

				// Move past the matched term
				startIndex = endIndex
			} else {
				// Not a whole word match, add one character and continue
				result += str.slice(startIndex, searchIndex + 1)
				startIndex = searchIndex + 1
			}
		}

		return result
	},

	/**
	 * Checks if a term at the given position is a whole word match.
	 * A whole word is defined as a sequence of characters that is not part of
	 * a larger word (i.e., surrounded by non-alphanumeric characters or at string boundaries).
	 *
	 * NOTE: This method has a corresponding server-side implementation in
	 * DatabaseAwareMessageSource.java (isWholeWordMatch). Any changes here
	 * should be synchronized with the Java version.
	 *
	 * @param {String} str - The original string
	 * @param {Number} start - Start index of the term
	 * @param {Number} end - End index of the term
	 * @return {Boolean} True if it's a whole word match
	 * @private
	 */
	_isWholeWordMatch: function(str, start, end) {
		return (start === 0 || !this.isAlphanumeric(str[start - 1])) &&
			(end === str.length || !this.isAlphanumeric(str[end]))
	},

	/**
	 * Checks if a term at the given position is part of a substitution placeholder.
	 * This ensures we don't replace parts of substitution variables like {0}.
	 *
	 * NOTE: This method has a corresponding server-side implementation in
	 * DatabaseAwareMessageSource.java (isPartOfSubstitutionPlaceholder). Any changes here
	 * should be synchronized with the Java version.
	 *
	 * @param {String} str - The original string
	 * @param {Number} start - Start index of the term
	 * @param {Number} end - End index of the term
	 * @return {Boolean} True if it's part of a substitution placeholder
	 * @private
	 */
	_isPartOfSubstitutionPlaceholder: function(str, start, end) {
		return (start > 0 && str[start - 1] === '{') ||
			(end < str.length && str[end] === '}')
	},

	/**
	 * Checks if a character is alphanumeric (letter or digit) in any language.
	 * This is a helper function that avoids using regular expressions for performance.
	 *
	 * NOTE: This method has a corresponding server-side implementation in
	 * DatabaseAwareMessageSource.java (isAlphanumeric). Any changes here
	 * should be synchronized with the Java version.
	 *
	 * @param {String} char - The character to check
	 * @return {Boolean} True if the character is alphanumeric
	 */
	isAlphanumeric: function(char) {
		// Convert to lowercase to handle both upper and lower case
		const lowerChar = char.toLocaleLowerCase()
		const code = lowerChar.charCodeAt(0)

		// Check if it's a letter in any language
		if (lowerChar !== lowerChar.toLocaleUpperCase()) {
			return true
		}

		// Check if it's a digit
		if (code >= 48 && code <= 57) {
			return true
		}

		return false
	},

	/**
	 * Matches the case of the replacement word with the original.
	 *
	 * NOTE: This method has a corresponding server-side implementation in
	 * DatabaseAwareMessageSource.java (matchWordCasing). Any changes here
	 * should be synchronized with the Java version.
	 *
	 * @param {String} original The original word
	 * @param {String} replacement The replacement word
	 * @return {String} The replacement word with matched first letter's case
	 */
	matchWordCasing: function(original, replacement) {
		if (replacement.charAt(0) === replacement.charAt(0).toUpperCase()) {
			return replacement
		}
		if (original.charAt(0) === original.charAt(0).toUpperCase()) {
			return _.capitalize(replacement)
		} else {
			return replacement
		}
	}
})

export default i18n

i18n.__filename__ = 'components/i18n.js'
